import { API_BASE, getAccessToken } from "@/lib/api/session";
import { t } from "@/i18n";
import {
  getPreference,
  setPreference,
} from "@/lib/capacitor/preferences";

export type WeeklySummaryResponse = {
  stats: {
    checkInCount: number;
    experimentCompleted: number;
    avgMood: number | null;
    avgEnergy: number | null;
    avgStress: number | null;
    topCategories: Array<{ category: string; count: number }>;
    daysActive: number;
  };
  summary: string;
  highlights: string[];
  suggestion: string;
  model: string;
  weekStart: string;
  weekEnd: string;
};

type CacheEnvelope = {
  savedAt: string;
  userKey: string;
  data: WeeklySummaryResponse;
};

const CACHE_KEY = "andrum.weekly.summary.v1";
/** Prefer network, but serve cache if fresher than this while revalidating. */
const FRESH_MS = 1000 * 60 * 60 * 6; // 6h

function userCacheKey(token: string): string {
  // Short fingerprint only for cache isolation (not secret storage)
  return token.slice(-16);
}

async function readCache(
  userKey: string
): Promise<{ data: WeeklySummaryResponse; ageMs: number } | null> {
  const raw = await getPreference(CACHE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CacheEnvelope;
    if (parsed.userKey !== userKey || !parsed.data) return null;
    const ageMs = Date.now() - new Date(parsed.savedAt).getTime();
    if (Number.isNaN(ageMs) || ageMs < 0) return null;
    return { data: parsed.data, ageMs };
  } catch {
    return null;
  }
}

async function writeCache(
  userKey: string,
  data: WeeklySummaryResponse
): Promise<void> {
  const envelope: CacheEnvelope = {
    savedAt: new Date().toISOString(),
    userKey,
    data,
  };
  await setPreference(CACHE_KEY, JSON.stringify(envelope));
}

async function fetchNetwork(): Promise<
  | { ok: true; data: WeeklySummaryResponse }
  | { ok: false; message: string; status?: number }
> {
  const token = await getAccessToken();
  if (!token) {
    return {
      ok: false,
      message: t("errors.loginForWeekly"),
      status: 401,
    };
  }

  try {
    const response = await fetch(`${API_BASE}/api/v1/insights/weekly`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const json = (await response.json().catch(() => ({}))) as {
        message?: string;
      };
      return {
        ok: false,
        status: response.status,
        message: json.message ?? "Kunde inte hämta veckosammanfattning.",
      };
    }
    const data = (await response.json()) as WeeklySummaryResponse;
    await writeCache(userCacheKey(token), data);
    return { ok: true, data };
  } catch {
    return {
      ok: false,
      message: t("errors.apiUnreachable"),
    };
  }
}

/**
 * Stale-while-revalidate weekly summary.
 * - Returns cached data immediately when available
 * - Refreshes in background when cache is older than FRESH_MS
 * - Falls back to cache on network failure
 */
export async function fetchWeeklySummary(options?: {
  force?: boolean;
}): Promise<
  | {
      ok: true;
      data: WeeklySummaryResponse;
      fromCache: boolean;
      revalidating?: boolean;
    }
  | { ok: false; message: string; status?: number }
> {
  const token = await getAccessToken();
  if (!token) {
    return {
      ok: false,
      message: t("errors.loginForWeekly"),
      status: 401,
    };
  }

  const userKey = userCacheKey(token);
  const cached = await readCache(userKey);
  const force = options?.force === true;

  if (!force && cached && cached.ageMs < FRESH_MS) {
    return { ok: true, data: cached.data, fromCache: true };
  }

  if (!force && cached) {
    // Return stale immediately; caller can refresh again if needed
    void fetchNetwork();
    return {
      ok: true,
      data: cached.data,
      fromCache: true,
      revalidating: true,
    };
  }

  const network = await fetchNetwork();
  if (network.ok) {
    return { ok: true, data: network.data, fromCache: false };
  }

  if (cached) {
    return {
      ok: true,
      data: cached.data,
      fromCache: true,
    };
  }

  return network;
}
